'use client';

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { extractYouTubeVideoId } from '../../../lib/youtube-api';

function YouTubeShortEmbed({ url, title, autoplay = false }) {
	const videoId = extractYouTubeVideoId(url);
	if (!videoId) {
		return (
			<div className="shortsModal__unavailable">
				<p>Vídeo indisponível</p>
				{url ? (
					<a href={url} target="_blank" rel="noopener noreferrer">
						Abrir no YouTube
					</a>
				) : null}
			</div>
		);
	}

	const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&modestbranding=1&rel=0&controls=1&playsinline=1`;

	return (
		<iframe
			src={embedUrl}
			title={title || 'Short do YouTube'}
			className="shortsModal__iframe"
			allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
			referrerPolicy="strict-origin-when-cross-origin"
			allowFullScreen
		/>
	);
}

export default function ShortsModal({
	isOpen,
	onClose,
	items = [],
	initialIndex = 0,
}) {
	const [mounted, setMounted] = useState(false);
	const [activeIndex, setActiveIndex] = useState(initialIndex);

	useEffect(() => {
		setMounted(true);
		return () => setMounted(false);
	}, []);

	useEffect(() => {
		if (isOpen) {
			setActiveIndex(
				Math.min(Math.max(0, initialIndex), Math.max(0, items.length - 1))
			);
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen, initialIndex, items.length]);

	useEffect(() => {
		if (!isOpen) return undefined;
		const onKey = (event) => {
			if (event.key === 'Escape') {
				onClose();
				return;
			}
			if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
				setActiveIndex((current) => Math.min(current + 1, items.length - 1));
			}
			if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
				setActiveIndex((current) => Math.max(current - 1, 0));
			}
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [isOpen, items.length, onClose]);

	const goPrev = useCallback(() => {
		setActiveIndex((current) => Math.max(current - 1, 0));
	}, []);

	const goNext = useCallback(() => {
		setActiveIndex((current) => Math.min(current + 1, items.length - 1));
	}, [items.length]);

	if (!mounted || !isOpen || !items.length) {
		return null;
	}

	const active = items[activeIndex];

	const modal = (
		<div
			className="shortsModal"
			role="dialog"
			aria-modal="true"
			aria-label="Shorts do YouTube"
		>
			<button
				type="button"
				onClick={onClose}
				className="shortsModal__close"
				aria-label="Fechar"
			>
				×
			</button>

			<div className="shortsModal__stage">
				{activeIndex > 0 ? (
					<button
						type="button"
						className="shortsModal__nav shortsModal__nav--prev"
						onClick={goPrev}
						aria-label="Short anterior"
					>
						‹
					</button>
				) : null}

				<div className="shortsModal__playerWrap">
					<YouTubeShortEmbed
						key={active.id}
						url={active.url}
						title={active.title}
						autoplay
					/>
				</div>

				{activeIndex < items.length - 1 ? (
					<button
						type="button"
						className="shortsModal__nav shortsModal__nav--next"
						onClick={goNext}
						aria-label="Próximo short"
					>
						›
					</button>
				) : null}
			</div>

			{active?.title ? (
				<p className="shortsModal__title">{active.title}</p>
			) : null}

			<p className="shortsModal__hint">
				Use as setas do teclado ou os botões laterais para navegar
			</p>
		</div>
	);

	return createPortal(modal, document.body);
}
